<template>
    <fieldset>
        <legend>My totals</legend>

        <table>
            <thead>
                <tr>
                    <td>Total Balances</td>
                    <td>Total Daily Profits</td>
                    <td class="weekly-profits">Total Weekly Profits</td>
                    <td>Total Monthly Profits</td>
                    <td>Total Claimable Tokens</td>
                </tr>
            </thead>
            <tbody>
                <tr v-if="totals !== null || isLoadingData">
                    <td>
                        <span v-if="totals !== null">
                            ≈ <strong>${{formatLargeNumber(totals.sumBalances)}}</strong><br /><br />
                            <strong>Total veCRV</strong><br />
                            <span v-if="totals.veCRV && totals.veCRV.amount > 0">{{formatLargeNumber(totals.veCRV.amount)}} veCRV<br />
                                locked for {{getHumanReadableDayDifference(totals.veCRV.lockedUntil)}}</span>
                            <span v-else>0 veCRV</span>
                        </span>
                        <span v-else-if="isLoadingData" class="loading line"></span>
                    </td>
                    <td>
                        <span v-if="totals !== null">
                            {{formatLargeNumber(totals.usdPerDay)}} USD/day<br />
                            <span v-if="totals.crvPerDay">+ {{formatLargeNumber(totals.crvPerDay)}} CRV/day<br /></span>
                            <span v-for="reward in totals.additionalRewardsPerDay">
                                <span>+ {{formatLargeNumber(reward.rewardsAmount)}} {{reward.name}}/day</span><br />
                            </span>
                            ≈ <strong>${{formatLargeNumber(totals.totalRewardsPerDayUsdValue)}}/day</strong>
                        </span>
                        <span v-else-if="isLoadingData" class="loading line"></span>
                    </td>
                    <td class="weekly-profits">
                        <span v-if="totals !== null">
                            {{formatLargeNumber(totals.usdPerDay * 7)}} USD/week<br />
                            <span v-if="totals.crvPerDay">+ {{formatLargeNumber(totals.crvPerDay * 7)}} CRV/week<br /></span>
                            <span v-for="reward in totals.additionalRewardsPerDay">
                                <span>+ {{formatLargeNumber(reward.rewardsAmount * 7)}} {{reward.name}}/week</span><br />
                            </span>
                            ≈ <strong>${{formatLargeNumber(totals.totalRewardsPerDayUsdValue * 7)}}/week</strong>
                        </span>
                        <span v-else-if="isLoadingData" class="loading line"></span>
                    </td>
                    <td>
                        <span v-if="totals !== null">
                            {{formatLargeNumber(totals.usdPerDay * 30)}} USD/month<br />
                            <span v-if="totals.crvPerDay">+ {{formatLargeNumber(totals.crvPerDay * 30)}} CRV/month<br /></span>
                            <span v-for="reward in totals.additionalRewardsPerDay">
                                <span>+ {{formatLargeNumber(reward.rewardsAmount * 30)}} {{reward.name}}/month</span><br />
                            </span>
                            ≈ <strong>${{formatLargeNumber(totals.totalRewardsPerDayUsdValue * 30)}}/month</strong>
                        </span>
                        <span v-else-if="isLoadingData" class="loading line"></span>
                    </td>
                    <td>
                        <span v-if="totals !== null">
                            <span>{{formatLargeNumber(totals.claimableCRV)}} CRV</span>
                            <span v-for="claimableReward in totals.claimableAdditionalRewards">
                                <br />
                                <span>+ {{formatLargeNumber(claimableReward.amount)}} {{claimableReward.name}}</span>
                            </span>
                            <br />
                            ≈ <strong>${{formatLargeNumber(totals.totalClaimableUsdValue)}}</strong>
                        </span>
                        <span v-else-if="isLoadingData" class="loading line"></span>
                    </td>
                </tr>
                <tr v-else><td colspan="5" class="empty-td">No data to display, please enter an address</td></tr>
            </tbody>
        </table>
    </fieldset>
</template>

<script>
    import Vue from 'vue';
    import config from '@/constants/config'
    import getFactoryV2Pools from '@/utils/data/getFactoryV2Pools'
    import getFactoryCryptoPools from '@/utils/data/getFactoryCryptoPools'
    import getCRVAPY, { getFactoCRVAPY } from '@/utils/data/getCRVAPY'
    import getAPY from '@/utils/data/getAPY'
    import { SortedTable, SortLink } from 'vue-sorted-table';
    import getPricesForAssets from '@/utils/data/getPricesForAssets'
    import getCurveRewards from '@/utils/data/getCurveRewards'
    import getOtherChainRewards from '@/utils/data/getOtherChainRewards'
    import getClaimableCRV from '@/utils/data/getClaimableCRV'
    import getClaimableAdditionalRewards from '@/utils/data/getClaimableAdditionalRewards'
    import getBalances from '@/utils/data/getBalances'
    import getFactoryOnlyPoolsBalances from '@/utils/data/getFactoryOnlyPoolsBalances'
    import getVeCRV from '@/utils/data/getVeCRV'
    import Pool from './Pool.vue';
    import pools, { getPools, isFactoryPoolDataLoaded } from '@/constants/pools';
    import { isFactoryAbiDataLoaded } from '@/constants/abis';
    import { arrayToHashmap } from '@/utils/helpers';
    import { sumBy, flattenArray } from '@/utils/Array';
    import { formatLargeNumber } from '@/utils/Number';
    import { getHumanReadableDayDifference } from '@/utils/Date';
    import { sleepUntil } from '@/utils/Async';
    import { contract } from '@/contract';
    import getSidechainAdditionalRewards from '@/utils/data/getSidechainAdditionalRewards'
    import getHarmonyRewards from '@/utils/data/getHarmonyRewards'

	export default {
		data: () => ({
            CRVprice: null,
            totals: null,
            additionalRewards: null,
            isLoadingData: false,
        }),

        props: {
            resolvedAddress: {
                type: String,
            },
        },

		async created() {
            this.initialize();
        },

        watch: {
            resolvedAddress() {
                this.initialize();
            }
        },

        methods: {
            formatLargeNumber,
            getHumanReadableDayDifference,

            async initialize() {
                await sleepUntil(() => (
                    isFactoryAbiDataLoaded() &&
                    isFactoryPoolDataLoaded() &&
                    this.resolvedAddress !== ''
                ));

                this.isLoadingData = true;
                this.CRVprice = null;
                this.totals = null;
                let CRVprice = 0; // Default to 0 for non-ethereum chains w/ no CRV distrib
                let CRVAPYs;
                let additionalRewards;
                let claimableCRV;
                let claimableAdditionalRewards;
                let veCRV;

                [claimableCRV, claimableAdditionalRewards] = await Promise.all([
                    getClaimableCRV(this.resolvedAddress, config.factoryV2HasGauges),
                    getClaimableAdditionalRewards(this.resolvedAddress, config.factoryV2HasGauges),
                ]);

                if (config.blockchainId === 'ethereum') {
                    ({
                        CRVprice,
                        CRVAPYs
                    } = await getCRVAPY(this.resolvedAddress, config.factoryV2HasGauges));

                    this.CRVprice = CRVprice;
                    this.CRVAPYs = CRVAPYs;

                    additionalRewards = await getCurveRewards();
                    this.additionalRewards = additionalRewards;

                    veCRV = await getVeCRV(this.resolvedAddress);
                } else {
                    ({
                        CRVprice,
                        CRVAPYs
                    } = await getFactoCRVAPY());

                    this.CRVprice = CRVprice;
                    this.CRVAPYs = CRVAPYs;
                }

                if (config.blockchainId === 'harmony') {
                  additionalRewards = await getHarmonyRewards();
                  this.additionalRewards = additionalRewards;
                }

                if (
                    config.blockchainId === 'xdai' ||
                    config.blockchainId === 'polygon' ||
                    config.blockchainId === 'kava' ||
                    config.blockchainId === 'celo' ||
                    config.blockchainId === 'avalanche' ||
                    config.blockchainId === 'fantom' ||
                    config.blockchainId === 'arbitrum' ||
                    config.blockchainId === 'moonbeam'
                ) {
                  additionalRewards = await getSidechainAdditionalRewards();
                  this.additionalRewards = additionalRewards;
                }

                const { daily_apy } = await getAPY();

                const mainPoolsBalancesData = await getBalances(this.resolvedAddress);
                const factoPoolsBalancesData = await getFactoryOnlyPoolsBalances(
                    this.resolvedAddress,
                    flattenArray(await Promise.all([getFactoryV2Pools(), getFactoryCryptoPools()])),
                );

                const balances = { ...mainPoolsBalancesData.balances, ...factoPoolsBalancesData.balances }; // Use new object instance for Vue to detect change
                const sumBalances = mainPoolsBalancesData.sumBalances + factoPoolsBalancesData.sumBalances;

                this.totals = this.getTotals({
                    CRVprice,
                    CRVAPYs,
                    additionalRewards,
                    claimableCRV,
                    claimableAdditionalRewards,
                    daily_apy,
                    sumBalances,
                    balances,
                    veCRV,
                });
                this.isLoadingData = false;
            },

            getTotals({
                CRVprice,
                CRVAPYs,
                additionalRewards,
                claimableCRV,
                claimableAdditionalRewards,
                daily_apy,
                sumBalances,
                balances,
                veCRV,
            }) {
                const poolTotals = getPools().map((pool, index) => ({
                    dailyAPY: (
                        pool.isFactoryOnlyPool ?
                            pool.apy :
                            (daily_apy && Number(daily_apy[index]))
                    ),
                    crvAPYs: CRVAPYs && CRVAPYs[pool.id],
                    crvAPYsSortingKey: CRVAPYs && CRVAPYs[pool.id] && CRVAPYs[pool.id][0],
                    claimableCRV: (claimableCRV && claimableCRV[pool.id]) || 0,
                    balance: balances && balances[pool.id],
                    claimableAdditionalRewards: (claimableAdditionalRewards && claimableAdditionalRewards[pool.id]) || [],
                    additionalRewards: this.poolAdditionalRewards(pool.id, additionalRewards),
                })).map((pool) => ({
                    claimableCRV: pool.claimableCRV,
                    claimableAdditionalRewards: pool.claimableAdditionalRewards,
                    usdPerDay: (pool.dailyAPY > 0 ? pool.dailyAPY : 0) / 100 / 365 * (pool.balance || 0),
                    crvPerDay: (
                        (!pool.isPendingGaugeVoteToStartCrvRewards && !pool.hasNoGauge && pool.crvAPYs && pool.crvAPYs[3]) ?
                            pool.crvAPYs[3] / 100 / 365 * (pool.balance || 0) / CRVprice : null
                    ),
                    additionalRewardsPerDay: pool.additionalRewards.map(({ name, rewardsAmount, price }) => ({
                        name,
                        price,
                        rewardsAmount: rewardsAmount / 100 / 365 * (pool.balance || 0) / price,
                    }))
                })).reduce((totals, poolStats) => ({
                    claimableCRV: totals.claimableCRV + poolStats.claimableCRV,
                    usdPerDay: totals.usdPerDay + poolStats.usdPerDay,
                    crvPerDay: totals.crvPerDay + poolStats.crvPerDay,
                    claimableAdditionalRewards: (
                        [
                            ...totals.claimableAdditionalRewards,
                            ...poolStats.claimableAdditionalRewards,
                        ].reduce((rewardsAccu, { name, amount, price }) => {
                            const prevRewardInfo = rewardsAccu.find((reward) => name === reward.name);
                            if (prevRewardInfo) {
                                prevRewardInfo.amount = prevRewardInfo.amount + amount;
                                return rewardsAccu;
                            }

                            if (amount > 0) {
                                return rewardsAccu.concat({ name, amount, price });
                            }

                            return rewardsAccu;
                        }, [])
                    ),
                    additionalRewardsPerDay: (
                        [
                            ...totals.additionalRewardsPerDay,
                            ...poolStats.additionalRewardsPerDay,
                        ].reduce((rewardsAccu, { name, rewardsAmount, price }) => {
                            const prevRewardInfo = rewardsAccu.find((reward) => name === reward.name);
                            if (prevRewardInfo) {
                                prevRewardInfo.rewardsAmount = prevRewardInfo.rewardsAmount + rewardsAmount;
                                return rewardsAccu;
                            }

                            if (rewardsAmount > 0) {
                                return rewardsAccu.concat({ name, rewardsAmount, price });
                            }

                            return rewardsAccu;
                        }, [])
                    ),
                }), {
                    claimableCRV: 0,
                    claimableAdditionalRewards: [],
                    usdPerDay: 0,
                    crvPerDay: 0,
                    additionalRewardsPerDay: [],
                });

                return {
                    ...poolTotals,
                    totalClaimableUsdValue: (
                        (poolTotals.claimableCRV > 0 ? (poolTotals.claimableCRV * CRVprice) : 0) +
                        sumBy(poolTotals.claimableAdditionalRewards, ({ amount, price }) => amount * price)
                    ),
                    totalRewardsPerDayUsdValue: (
                        poolTotals.usdPerDay +
                        (poolTotals.crvPerDay > 0 ? (poolTotals.crvPerDay * CRVprice) : 0) +
                        sumBy(poolTotals.additionalRewardsPerDay, ({ rewardsAmount, price }) => rewardsAmount * price)
                    ),
                    sumBalances,
                    veCRV,
                };
            },

            poolAdditionalRewards(poolId, additionalRewards) {
                const pool = getPools().find(({ id }) => poolId === id);

                return (
                    pool.isFactoryOnlyPool ?
                        pool.additionalRewards.map(({ symbol, tokenPrice, apy }) => ({
                            name: symbol,
                            rewardsAmount: apy,
                            price: tokenPrice,
                        })) :
                        pool.additionalRewards.map(({ key, name, amountDataKey }) => ({
                            name,
                            rewardsAmount: (
                                additionalRewards?.[key || name] ? additionalRewards[key || name].rewards :
                                this.additionalRewards?.[key || name] ? this.additionalRewards[key || name].rewards :
                                null
                            ),
                            price: (
                                additionalRewards?.[key || name] ? additionalRewards[key || name].price :
                                this.additionalRewards?.[key || name] ? this.additionalRewards[key || name].price :
                                null
                            ),
                        }))
                );
            },
        },
	}
</script>

<style scoped>
    fieldset legend {
        text-align: center;
    }

    table {
        width: 100%;
    }

    thead {
        font-weight: bold;
    }

    tr {
        vertical-align: top;
    }

    .empty-td {
        text-align: center;
        font-style: italic;
    }

    strong::before,
    strong::after {
        content: none;
    }

    @media only screen and (max-device-width : 900px) {
        .weekly-profits {
            display: none;
        }
    }
</style>
